'use client';

import { getAlphabetLabel } from '@/shared-utilities';

import SurveyAiFormsAiSectionQuestionTitle from './survey-ai-forms-ai-section-question-title';
import { useAiSelectQuestion } from './survey-ai-forms-ai-section-question.hooks';
import type { AISectionQuestionsBaseProps } from './survey-ai-forms-ai-section-questions.types';
import SurveyAiFormsInputComposer from './survey-ai-forms-input-composer';

interface Props extends AISectionQuestionsBaseProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  loading?: boolean;
}

export default function SurveyAiFormsAiSectionSelectQuestion({
  question,
  value,
  transcriptValue = '',
  onChange,
  onTranscriptChange,
  onSubmit,
  onNext,
  hasValue = false,
  responseMode,
  isActive = true,
  onFocus,
  registerVoiceStopCallback,
  loading = false,
  isMobile = false,
  isUserVoiceEnabled = false,
}: Props) {
  // Use AI select question hook for business logic
  const {
    isListening,
    options,
    isOptionSelected,
    handleOptionClick,
    handleInputComposerSubmit,
    canSubmit,
  } = useAiSelectQuestion({
    question,
    value,
    transcriptValue,
    onChange,
    onSubmit,
    onNext,
    hasValue,
  });

  return (
    <div
      data-test='select-question'
      className='flex w-full flex-col gap-6 md:gap-8'
    >
      {/* Question Title */}
      <SurveyAiFormsAiSectionQuestionTitle
        text={question.text}
        assets={question.assets}
      />

      {/* Options Grid - 1 column on mobile, 2 columns on desktop */}
      <div className='grid w-full gap-2 lg:grid-cols-2'>
        {options.map((option, index) => {
          const isSelected = isOptionSelected(option.value);
          const letter = getAlphabetLabel(index);

          return (
            <button
              key={option.id}
              type='button'
              onClick={() => handleOptionClick(option.value)}
              data-selected={isSelected}
              data-test='select-option'
              className='flex min-h-12 min-w-62.5 cursor-pointer items-center justify-between gap-1 rounded-[48px] px-6 py-2 backdrop-blur-xs backdrop-filter transition-colors data-[selected=false]:bg-(--brand-bg-unselected) data-[selected=true]:bg-(--brand-primary) data-[selected=true]:shadow-[0px_4px_24px_0px_rgba(52,52,111,0.24)]'
            >
              <p
                className='text-left [font-family:var(--brand-font)] text-sm/5 font-bold whitespace-pre-line data-[selected=false]:text-(--brand-text-unselected) data-[selected=true]:text-(--brand-primary-contrast)'
                data-selected={isSelected}
                data-test='select-option-label'
              >
                {option.label}
              </p>
              <div
                data-selected={isSelected}
                data-test='select-option-letter'
                className='min-size-6 flex shrink-0 flex-col items-center justify-center rounded-md px-1.5 py-0.5 data-[selected=false]:bg-white data-[selected=true]:bg-(--brand-bg-unselected)'
              >
                <p
                  data-selected={isSelected}
                  className='w-full text-center [font-family:var(--brand-font)] text-xs/4 font-normal data-[selected=false]:text-black data-[selected=true]:text-(--brand-text-unselected)'
                >
                  {letter}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Input Composer - for AI transcript input */}
      <SurveyAiFormsInputComposer
        value={transcriptValue}
        onChange={onTranscriptChange}
        onSubmit={handleInputComposerSubmit}
        responseMode={responseMode}
        isActive={isActive}
        onFocus={onFocus}
        registerVoiceStopCallback={registerVoiceStopCallback}
        instanceId={question.id}
        canSubmit={canSubmit}
        isListening={isListening}
        questionType={question.type}
        loading={loading}
        isMobile={isMobile}
        isUserVoiceEnabled={isUserVoiceEnabled}
      />
    </div>
  );
}
